/**
 * Rebuy dynamic bundle: show the saving next to the subtotal.
 *
 * Rebuy prints the subtotal as a sale price plus a struck-through compare-at
 * price, but never the saving between them. The two figures already track
 * the checkboxes (select two products, select all, swap a variant), so the
 * badge is derived from whatever Rebuy has rendered rather than from our own
 * price maths — that way it can never disagree with the subtotal beside it.
 *
 * The wording comes from the "Frequently bought together" theme settings,
 * printed by theme.liquid as #jg-bundle-discount-config: [amount] becomes the
 * amount saved in the shop's money format and [percent] the percentage.
 *
 * Like rebuy-bundle-images.js this has to survive Rebuy's Vue re-renders, so
 * the badge is re-created whenever it goes missing and its text is only
 * touched when the value actually changes.
 */

(function () {
  const WIDGET = '.rebuy-widget.widget-type-dynamic-bundle';
  const PRICE_ROW = '.rebuy-bundle__actions-price';
  const VALUE = '.rebuy-bundle__actions-price-value';
  const BADGE_CLASS = 'jg-bundle-discount';
  // Below this the badge reads as noise ("Save 0%"), so it stays hidden.
  const MIN_PERCENT = 1;
  const DEFAULT_TEXT = 'Save [amount] ([percent]%)';
  const config = readConfig();

  let frame = 0;

  function readConfig() {
    const node = document.getElementById('jg-bundle-discount-config');
    let parsed = {};

    try {
      parsed = node ? JSON.parse(node.textContent) : {};
    } catch (error) {
      parsed = {};
    }

    return {
      text: typeof parsed.text === 'string' && parsed.text.trim() ? parsed.text : DEFAULT_TEXT,
      moneyFormat:
        typeof parsed.moneyFormat === 'string' && parsed.moneyFormat ? parsed.moneyFormat : '${{amount}}',
    };
  }

  /* Same placeholders as Shopify's money formats, see product-custom.js. */
  function formatMoney(cents) {
    const amount = cents / 100;

    function withDelimiters(precision, thousands, decimal) {
      const parts = amount.toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }

    return config.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(2, "'", '.');
        default:
          return withDelimiters(2, ',', '.');
      }
    });
  }

  /* Rebuy renders money through the shop's own formatter, so the separators
     follow the storefront locale rather than a fixed en-US shape. */
  function parseMoney(text) {
    const cleaned = String(text || '').replace(/[^0-9.,]/g, '');
    if (!cleaned) return NaN;

    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const separator = Math.max(lastDot, lastComma);
    let normalised = cleaned;

    if (separator !== -1) {
      const decimals = cleaned.length - separator - 1;
      // "1,000" / "1.000" is a thousands separator; "1,00" / "1.00" is not.
      if (decimals === 3 && (lastDot === -1 || lastComma === -1)) {
        normalised = cleaned.replace(/[.,]/g, '');
      } else {
        normalised =
          cleaned.slice(0, separator).replace(/[.,]/g, '') + '.' + cleaned.slice(separator + 1);
      }
    }

    const value = parseFloat(normalised);
    return isNaN(value) ? NaN : value;
  }

  function moneyValue(node) {
    return node ? parseMoney(node.textContent) : NaN;
  }

  /* Saving on the current selection, or null when there is nothing to show.
     Cents keep the subtraction free of floating point noise. */
  function saving(row) {
    const value = row.querySelector(VALUE) || row;
    const compare = moneyValue(value.querySelector('.rebuy-money.compare-at'));
    const sale = moneyValue(value.querySelector('.rebuy-money:not(.compare-at)'));

    if (isNaN(compare) || isNaN(sale) || compare <= 0 || sale >= compare) return null;

    const cents = Math.round(compare * 100) - Math.round(sale * 100);
    const percent = Math.round((cents / Math.round(compare * 100)) * 100);
    return percent >= MIN_PERCENT ? { cents: cents, percent: percent } : null;
  }

  function savingText(result) {
    return config.text
      .replace(/\[amount\]/g, formatMoney(result.cents))
      .replace(/\[percent\]/g, String(result.percent));
  }

  function getBadge(row) {
    let badge = row._jgDiscountBadge;

    if (!badge || badge.parentNode !== row) {
      badge = row.querySelector(':scope > .' + BADGE_CLASS);
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = BADGE_CLASS;
      badge.setAttribute('aria-live', 'polite');
    }

    // Rebuy patches this row, so re-attach rather than assume it survived.
    if (badge.parentNode !== row) row.appendChild(badge);
    row._jgDiscountBadge = badge;
    return badge;
  }

  function render(row) {
    const badge = getBadge(row);
    const result = saving(row);
    const text = result ? savingText(result) : '';

    if (badge.textContent !== text) badge.textContent = text;
    if (badge.hidden !== !result) badge.hidden = !result;
  }

  function inBadge(node) {
    const element = node && node.nodeType === 1 ? node : node && node.parentNode;
    return !!(element && element.closest && element.closest('.' + BADGE_CLASS));
  }

  /* True for the two mutations we cause ourselves: rewriting the badge text
     (target is the badge) and attaching the badge (target is the row, so the
     added node has to be checked instead). A record that *removes* the badge
     is deliberately not ours — that is Rebuy patching it away, and the sync
     it schedules is what puts the badge back. */
  function isSelfInflicted(record) {
    if (inBadge(record.target)) return true;

    const added = record.addedNodes;
    return (
      record.removedNodes.length === 0 &&
      added.length === 1 &&
      added[0].nodeType === 1 &&
      added[0].classList.contains(BADGE_CLASS)
    );
  }

  function observe(bundle) {
    if (bundle._jgDiscountObserver) return;

    const observer = new MutationObserver(function (records) {
      for (let i = 0; i < records.length; i++) {
        // Our own badge updates must not re-trigger a sync.
        if (!isSelfInflicted(records[i])) {
          schedule();
          return;
        }
      }
    });

    observer.observe(bundle, { childList: true, subtree: true, characterData: true });
    bundle._jgDiscountObserver = observer;
  }

  function sync() {
    const bundles = document.querySelectorAll(WIDGET + ' .rebuy-bundle');

    for (let i = 0; i < bundles.length; i++) {
      const row = bundles[i].querySelector(PRICE_ROW);
      if (!row) continue;

      render(row);
      observe(bundles[i]);
    }
  }

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = 0;
      sync();
    });
  }

  /* Rebuy injects its widgets asynchronously, so watch for them arriving. */
  new MutationObserver(function (records) {
    for (let i = 0; i < records.length; i++) {
      const added = records[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.closest(WIDGET) || node.querySelector(WIDGET)) {
          schedule();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('rebuy:widget.ready', schedule);
  document.addEventListener('rebuy:widget.rendered', schedule);
  schedule();
})();
